import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate } from "@/types";

import { type Comment } from "@/modules/news/user/types";

export type CommentList = Paginate<Comment<true>>;

export const comments = createQueryKeys("news-comments", {
  all: (page?: number) => [{ page }],
  news: (newsId: number, page?: number) => [{ newsId, page }],
  replies: (commentId: number, page?: number) => [{ commentId, page }],
});

const include = "user";
const latestInclude = "user,news";

export const fetchLatestCommentsQueryOptions = (
  page: number = 0,
  query?: string,
) =>
  queryOptions({
    ...comments.all(page),
    queryFn: () =>
      api
        .get<Paginate<Comment<true, true>>>(`/news/comments`, {
          params: { page, query, include: latestInclude },
        })
        .then((response) => response.data),
  });

export const useFetchLatestComments = () =>
  usePaginateQuery((page) => fetchLatestCommentsQueryOptions(page));

export const fetchCommentsQueryOptions = (newsId: number, page: number = 0) =>
  queryOptions({
    ...comments.news(newsId, page),
    queryFn: () =>
      api
        .get<CommentList>(`/news/${newsId}/comments`, {
          params: { page, include },
        })
        .then((response) => response.data),
  });

export const useFetchComments = (newsId: number) =>
  usePaginateQuery((page) => fetchCommentsQueryOptions(newsId, page));

export const fetchCommentReplies = (commentId: number, page: number = 0) =>
  queryOptions({
    ...comments.replies(commentId, page),
    queryFn: () =>
      api
        .get<{
          data: Comment[];
        }>(`/news/comments/${commentId}/replies?include=${include}`)
        .then((response) => response.data),
  });

export const useFetchCommentReplies = (commentId: number) =>
  usePaginateQuery((page) => fetchCommentReplies(commentId, page));

export interface CommentRequest {
  content: string;
}

export const useCreateComment = (newsId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CommentRequest) =>
      api
        .post<
          Comment<true>
        >(`/news/${newsId}/comments?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: comments.all._def,
      });
      queryClient.invalidateQueries(comments.news(newsId));
    },
  });
};

export const useCreateCommentReply = (commentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CommentRequest) =>
      api
        .post<
          Comment<true>
        >(`/news/comments/${commentId}/replies?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: comments.all._def,
      });
      queryClient.invalidateQueries(comments.replies(commentId));
    },
  });
};
